import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import {
  AlertCircle,
  ArrowRight,
  CarFront,
  Cog,
  Copy,
  DollarSignIcon,
  FileJson,
  Info,
  ListCheck,
  Loader,
  Loader2,
  ShieldCheck,
  User2,
} from "lucide-react";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isAlphanumeric, isNumeric } from "validator";
import { verifyPennyUpiCheckValidation } from "../../../Services/ApiTestService/apiTestService";
import { NumbersSharp } from "@mui/icons-material";

function PennyUpiCheckValidation() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [accNumber, setAccNumber] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(null);
  const { serviceName } = useService();
  const [ifsc, setIfsc] = useState("");
  const [benificiaryName, setBenificiaryName] = useState("");

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlpha = (value) => {
    if (!value) return false;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const isNumber = (value) => {
    if (!value) return false;

    return isNumeric(value, { no_symbols: true });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(() => showSuccessToast("Copied!"));

    setIsJsonCopied(true);
  };

  const handleChasisToRc = async () => {
    if (!profileData) return;

    // const validateAcc = isAlpha(accNumber);
    const validateifsc = isAlpha(ifsc);
    const validateNumber = isNumber(accNumber);

    if (!ifsc) {
      return setInputError("ifsc Required");
    } else if (!validateifsc) {
      return setInputError("ifsc should be alpha numeric");
    } else if (!accNumber) {
      return setInputError("account number Required");
    } else if (!validateNumber) {
      return setInputError("please enter number in account number field");
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await verifyPennyUpiCheckValidation({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        accNo: accNumber.trim(),
        ifsc: ifsc.trim()?.toUpperCase(),
        benificiaryName
      });

      if (res) {
        apiResponse = res;
      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start pb-14">
        {/* ---------------- FORM ---------------- */}
        <div className="max-w-4xl w-full mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-100">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-slate-100 pb-5">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <DollarSignIcon size={24} />
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-800">
                Penny UPI Check Validation
              </h2>
              <p className="text-sm text-slate-500">
                Verify bank details using Account number, IFSC code, beneficiary with Penny UPI.
              </p>
            </div>
          </div>

          {/* Inputs */}
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2   lg:grid-cols-3">
            {/* IFSC code */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <ShieldCheck size={17} className="text-green-600" />
                IFSC Code
                <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                value={ifsc}
                onChange={(e) => setIfsc(e.target.value)}
                placeholder="Eg: SBIN0006162"
                className="w-full rounded-xl border uppercase border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>

            {/* Account Number */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <NumbersSharp size={13} className="text-blue-600" />
                Account Number                <span className="text-red-500">*</span>

              </label>

              <input
                type="text"
                value={accNumber}
                onChange={(e) => setAccNumber(e.target.value)}
                placeholder="Eg: 8585854854"
                className="w-full rounded-xl border uppercase border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>
            {/* Beneficiary */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <User2 size={17} className="text-blue-600" />
                Beneficiary Name
              </label>

              <input
                type="text"
                value={benificiaryName}
                onChange={(e) => setBenificiaryName(e.target.value)}
                placeholder="Eg: Abhishek Kumar"
                className="w-full rounded-xl border uppercase border-slate-300 px-4 py-3 text-sm transition-all duration-200 placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none"
              />
            </div>
          </div>

          {/* Error */}
          {inputError && (
            <div className="mt-5 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm font-medium">{inputError}</span>
            </div>
          )}

          {/* Button */}
          <div className="mt-8 flex justify-end">
            <button
              disabled={isLoading || (!ifsc || !accNumber)}
              onClick={handleChasisToRc}
              className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-7 py-3 font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  Verify Penny UPI
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </>
              )}
            </button>
          </div>
        </div>

        {/* response message */}

        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-green-600"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div className="w-full flex justify-center">
          <div className="bg-green-100 max-w-3xl border border-green-400 text-green-700 p-5 mt-12 rounded-2xl">
            <h3 className="font-bold flex items-center justify-start gap-2 text-lg mb-2">
              <Info /> Important Instructions{" "}
              <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid IFSC code and Account Number and Beneficiary name. Clicking the
              Verify button. After successful verification, the system will
              return the response in structured format, including contact
              details associated with the provided details. You may review and
              copy the data for your reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-10 w-full max-w-7xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 relative text-green-300 p-4 rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>
              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8" />{" "}
              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            <div className="bg-gray-200 p-5 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2 font-bold text-blue-500 mb-1">
                <ListCheck />
                Full Data
              </h2>
              <hr className="mb-8" />

              {response?.data?.code === "200" && response?.data?.model && (
  <div className="mb-6 p-5 bg-white rounded-xl shadow-md border border-gray-100">
    {/* Header Section */}
    <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-4">
      <h3 className="text-lg font-semibold text-gray-800">
        Bank Account Verification
      </h3>
      <span
        className={`px-3 py-1 rounded-full text-xs font-bold tracking-wide ${
          response.data.model.status === "SUCCESS"
            ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
            : "bg-red-100 text-red-700 border border-red-200"
        }`}
      >
        {response.data.model.status}
      </span>
    </div>

    {/* Data Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5 gap-x-4">
      {/* Beneficiary Name */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
          Beneficiary Name
        </p>
        <p className="text-sm font-medium text-gray-900">
          {response.data.model.beneficiaryName || "-"}
        </p>
      </div>

      {/* Name Match Status */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
          Name Match Status
        </p>
        <p
          className={`text-sm font-medium ${
            response.data.model.isNameMatch
              ? "text-emerald-600"
              : "text-amber-600"
          }`}
        >
          {response.data.model.isNameMatch ? "Matched" : "Not Matched"}
        </p>
      </div>

      {/* Payment Mode */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
          Payment Mode
        </p>
        <p className="text-sm font-medium text-gray-900">
          {response.data.model.paymentMode || "-"}
        </p>
      </div>

      {/* RRN */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
          Retrieval Ref No. (RRN)
        </p>
        <p className="text-sm font-medium text-gray-900">
          {response.data.model.rrn || "-"}
        </p>
      </div>

      {/* Transaction ID */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
          Transaction ID
        </p>
        <p className="text-sm font-medium text-gray-900 break-all">
          {response.data.model.transactionId || "-"}
        </p>
      </div>

      {/* Client Reference Number */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
          Client Ref Number
        </p>
        <p className="text-sm font-medium text-gray-900 break-all">
          {response.data.model.clientRefNum || "-"}
        </p>
      </div>
    </div>
  </div>
)}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default PennyUpiCheckValidation;
